import { useCallback, useState } from "react";
import {
  lookupPublicMemberByEmail,
  verifyPublicMemberByEmail,
  MemberLookupAthlete,
  MemberVerifyResult,
  PublicMemberResource,
} from "../api/publicMemberLookupAPI";

/**
 * The "who are you?" state machine for the PUBLIC registration pages
 * (camps, clinics, events).
 *
 * WHY THIS IS SHARED
 * ------------------
 * `handleEmailBlur`, `applyMemberData`, `handleVerifyPassword`,
 * `handleSkipPasswordPrompt` and `toggleAthleteSelection` existed as three
 * byte-identical copies across PublicCampPage, PublicClinicPage and
 * PublicEventPage — roughly 120 lines duplicated three ways, wrapped around a
 * password-authenticated identity check.
 *
 * That check is a SECURITY boundary: member details are only released after
 * `verifyMemberByEmail` succeeds (see CLAUDE.md, "Detail-extraction has moved
 * behind password auth"). With three copies, the next change to it — a lockout,
 * a new status code, a narrower payload — lands on one page and leaves the
 * other two on the old path. Same "fixed on 1 of N" mechanism that produced six
 * enrollment regressions.
 *
 * `contactInfo` and `participants` stay owned by the page (the whole form reads
 * them); the hook drives them through the setters passed in.
 */

/** The participant row shape the three pages submit. */
export interface PublicParticipant {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  athlete_id?: string;
}

interface Options {
  /** Tenant slug from the route. */
  clubid: string;
  /** Which public surface — selects the API endpoint. */
  resource: PublicMemberResource;
  setContactInfo: (updater: (prev: any) => any) => void;
  setParticipants: (updater: (prev: PublicParticipant[]) => PublicParticipant[]) => void;
}

// Athletes from `verify-member` carry either a real UUID (sourced from the
// athletes table — set athlete_id) or a synthetic "first-last-dob" key (sourced
// from prior registration history — leave athlete_id null so the row stays a
// manual entry, since a synthetic id would fail the registrations.athlete_id FK
// to athletes(id)).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isAthleteUuid = (v: string | undefined | null): boolean => !!v && UUID_RE.test(v);

const participantFromAthlete = (a: MemberLookupAthlete): PublicParticipant => ({
  first_name: a.first_name,
  last_name: a.last_name,
  date_of_birth: a.date_of_birth || "",
  gender: (a.gender || "").toLowerCase(),
  athlete_id: isAthleteUuid(a.id) ? a.id : undefined,
});

const syntheticKey = (p: { first_name: string; last_name: string; date_of_birth: string }) =>
  `${p.first_name}-${p.last_name}-${p.date_of_birth}`;

export function usePublicMemberLookup({
  clubid,
  resource,
  setContactInfo,
  setParticipants,
}: Options) {
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [memberFound, setMemberFound] = useState(false);
  const [accountType, setAccountType] = useState<"member" | "customer" | null>(null);
  const [membershipId, setMembershipId] = useState<string | null>(null);
  // The backend's signed proof that this visitor verified as membershipId.
  // Sent back as `member_token` with every registration that names the
  // membership — without it the anonymous register routes refuse the id.
  const [memberToken, setMemberToken] = useState<string | null>(null);
  const [memberAthletes, setMemberAthletes] = useState<MemberLookupAthlete[]>([]);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<Set<string>>(new Set());
  const [passwordPromptOpen, setPasswordPromptOpen] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const resetMemberState = useCallback(() => {
    setMemberFound(false);
    setAccountType(null);
    setMembershipId(null);
    setMemberToken(null);
    setMemberAthletes([]);
    setSelectedAthleteIds(new Set());
    setPasswordPromptOpen(false);
    setVerifyPassword("");
    setVerifyError(null);
  }, []);

  /**
   * Existence probe on email blur. Deliberately non-blocking: a lookup failure
   * must not stop someone registering as a new visitor, so it is caught here
   * rather than surfaced. This is NOT the silent-swallow anti-pattern — the
   * degraded path (fill the form manually) is the correct, complete behaviour,
   * not an empty screen.
   */
  const handleEmailBlur = useCallback(
    async (email: string) => {
      const trimmed = (email || "").trim();
      if (!trimmed) return;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmed)) return;

      setEmailLookupLoading(true);
      try {
        const result = await lookupPublicMemberByEmail(resource, clubid, trimmed);
        if (result?.found && result.requires_password) {
          setPasswordPromptOpen(true);
          setAccountType(result.account_type || "member");
        } else {
          resetMemberState();
        }
      } catch {
        // Non-blocking by design — the user can still fill the form.
      } finally {
        setEmailLookupLoading(false);
      }
    },
    [clubid, resource, resetMemberState]
  );

  const applyMemberData = useCallback(
    (result: MemberVerifyResult) => {
      if (!result?.found || !result.data) return;
      setMemberFound(true);
      setAccountType(result.account_type || "member");
      setMembershipId(result.data.membership_id || null);
      setMemberToken(result.data.member_token || null);
      setMemberAthletes(result.data.athletes || []);
      setContactInfo((prev: any) => ({
        ...prev,
        contact_name: result.data!.contact_name || prev.contact_name,
        contact_phone: result.data!.contact_phone || prev.contact_phone,
      }));

      // Sole athlete is auto-selected as a convenience. A synthetic (non-UUID)
      // id still selects, but contributes no participant row — it can't satisfy
      // the athlete_id FK.
      if (result.data.athletes?.length === 1) {
        const a = result.data.athletes[0];
        setSelectedAthleteIds(new Set([a.id]));
        setParticipants(() => (isAthleteUuid(a.id) ? [participantFromAthlete(a)] : []));
      } else {
        setParticipants(() => []);
      }
    },
    [setContactInfo, setParticipants]
  );

  const handleVerifyPassword = useCallback(
    async (email: string) => {
      const trimmed = (email || "").trim();
      if (!trimmed || !verifyPassword) {
        setVerifyError("Enter your password to load your account.");
        return;
      }
      setVerifyLoading(true);
      setVerifyError(null);
      try {
        const result = await verifyPublicMemberByEmail(
          resource,
          clubid,
          trimmed,
          verifyPassword
        );
        applyMemberData(result);
        setPasswordPromptOpen(false);
        setVerifyPassword("");
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) {
          setVerifyError(
            "Incorrect password. Try again or fill the form below to register as a new visitor."
          );
        } else if (status === 429) {
          setVerifyError("Too many attempts. Please try again in a few minutes.");
        } else {
          setVerifyError("Couldn't verify your account. Try again or continue as a new visitor.");
        }
      } finally {
        setVerifyLoading(false);
      }
    },
    [applyMemberData, clubid, resource, verifyPassword]
  );

  const handleSkipPasswordPrompt = useCallback(() => {
    setPasswordPromptOpen(false);
    setVerifyPassword("");
    setVerifyError(null);
  }, []);

  const toggleAthleteSelection = useCallback(
    (athlete: MemberLookupAthlete) => {
      setSelectedAthleteIds((prev) => {
        const next = new Set(prev);
        if (next.has(athlete.id)) next.delete(athlete.id);
        else next.add(athlete.id);
        return next;
      });

      // Real athletes.id UUIDs match by athlete_id. Synthetic ids (from
      // registration-history-only customer entries) match on first/last/dob
      // because athlete_id can't be set without failing the FK to athletes(id).
      const useUuid = isAthleteUuid(athlete.id);
      const matchKey = useUuid ? null : syntheticKey({
        first_name: athlete.first_name,
        last_name: athlete.last_name,
        date_of_birth: athlete.date_of_birth || "",
      });

      setParticipants((prev) => {
        const exists = useUuid
          ? prev.find((p) => p.athlete_id === athlete.id)
          : prev.find((p) => !p.athlete_id && syntheticKey(p) === matchKey);
        if (exists) {
          return useUuid
            ? prev.filter((p) => p.athlete_id !== athlete.id)
            : prev.filter((p) => !(!p.athlete_id && syntheticKey(p) === matchKey));
        }
        return [...prev, participantFromAthlete(athlete)];
      });
    },
    [setParticipants]
  );

  return {
    // state
    emailLookupLoading,
    memberFound,
    accountType,
    membershipId,
    memberToken,
    memberAthletes,
    selectedAthleteIds,
    passwordPromptOpen,
    verifyPassword,
    verifyError,
    verifyLoading,
    // setters the pages still drive directly
    setVerifyPassword,
    setSelectedAthleteIds,
    // handlers
    resetMemberState,
    handleEmailBlur,
    applyMemberData,
    handleVerifyPassword,
    handleSkipPasswordPrompt,
    toggleAthleteSelection,
  };
}
